import { useCallback, useEffect, useRef, useState } from "react";
import BoBDocumentsAPI from "../../../api/BoBDocumentsAPI";

/**
 * Documents for one BoB client, keyed by the row's `anchor_key` like useBoBNotes, and loaded on
 * row open for the same reason: the section header shows the count while collapsed. Switching
 * rows clears the previous client's documents at once, and late responses for a row that is
 * no longer open are dropped.
 */
export default function useBoBDocuments(clientKey, period = null) {
	const [documents, setDocuments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const currentId = useRef(clientKey);

	const refresh = useCallback(async () => {
		currentId.current = clientKey;

		if (!clientKey) {
			setDocuments([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		setError("");

		try {
			const data = await BoBDocumentsAPI.getDocuments(clientKey);
			if (currentId.current !== clientKey) return;
			setDocuments(Array.isArray(data.documents) ? data.documents : []);
		} catch (e) {
			if (currentId.current !== clientKey) return;
			setError(e.message);
		} finally {
			if (currentId.current === clientKey) setLoading(false);
		}
	}, [clientKey]);

	useEffect(() => {
		setDocuments([]);
		setError("");
		refresh();
	}, [refresh]);

	/** Resolves with the stored document; rejects with the server's message. */
	const uploadDocument = useCallback(
		async (file) => {
			const id = clientKey;
			// Filed under the cutoff being viewed, so a document says which month it came in.
			const data = await BoBDocumentsAPI.upload(id, file, period);
			if (currentId.current === id && data.document) {
				setDocuments((prev) => [data.document, ...prev]);
			}
			return data.document;
		},
		[clientKey, period]
	);

	const deleteDocument = useCallback(async (id) => {
		await BoBDocumentsAPI.deleteDocument(id);
		setDocuments((prev) => prev.filter((d) => d.id !== id));
	}, []);

	return { documents, loading, error, uploadDocument, deleteDocument };
}
